import client from './client.js';

/**
 * Unified payment API — maps to backend PaymentTransactionController
 * (@RequestMapping("/api/v1/payments")).
 *
 * Request/response shapes mirror the Java DTOs:
 *  - PaymentTransactionRequest  -> initiatePayment / initiatePayout
 *  - PaymentTransactionResponse <- every endpoint
 */

export function initiatePayment(request) {
  return client.post('payments/initiate', request).then((r) => r.data);
}

export function initiatePayout(request) {
  return client.post('payments/payout/initiate', request).then((r) => r.data);
}

export function getPaymentStatus(transactionReference) {
  // Backend: @GetMapping("/payments/{reference}") — no /status suffix.
  return client.get(`payments/${encodeURIComponent(transactionReference)}`).then((r) => r.data);
}

export function queryProviderStatus(transactionReference) {
  return client.post('payments/query-status', { transactionReference }).then((r) => r.data);
}

/**
 * Entity-scoped transaction register (owner/manager reporting).
 * Backend: GET /payments/entity/{entityType}/{entityId}
 *
 * @param {string} entityType   e.g. 'CAMPAIGN' | 'CONTRIBUTION'
 * @param {string} entityId     UUID of the entity
 * @param {Object} [filters]
 * @param {string} [filters.from]    ISO datetime, inclusive lower bound
 * @param {string} [filters.to]      ISO datetime, inclusive upper bound
 * @param {Array}  [filters.status]  PaymentTransactionStatus whitelist
 * @param {Object} [pageable]       { page, size, sort } forwarded to Spring
 */
export function getEntityTransactions(entityType, entityId, filters = {}, pageable = {}) {
  const params = {
    from: filters.from,
    to: filters.to,
    ...(filters.status && filters.status.length
      ? { status: filters.status.join(',') }
      : {}),
    ...pageable,
  };
  return client
    .get(`payments/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`, { params })
    .then((r) => r.data);
}

/**
 * Per-status aggregates for the same register — summary headers
 * without shipping rows.
 * Backend: GET /payments/entity/{entityType}/{entityId}/summary
 */
export function getEntityTransactionSummary(entityType, entityId, filters = {}) {
  return client
    .get(`payments/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/summary`, {
      params: { from: filters.from, to: filters.to },
    })
    .then((r) => r.data);
}

/**
 * Format TRX-yyyyMMdd-HHmmss-XXXXXX — mirrors the backend generator so
 * externally supplied references stay consistent. Reusing the same
 * reference on retries makes operations idempotent (unique DB constraint).
 */
export function newTransactionReference() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `TRX-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(
    d.getMinutes()
  )}${pad(d.getSeconds())}-${rand}`;
}

export default {
  initiatePayment,
  initiatePayout,
  getPaymentStatus,
  queryProviderStatus,
  getEntityTransactions,
  getEntityTransactionSummary,
  newTransactionReference,
};
